import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Button,
  Text,
  Box,
  Flex,
  Spinner,
  IconButton,
  VStack,
  Heading,
  Divider,
  useToast,
  useColorModeValue,
  SimpleGrid,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { FaHeart, FaMoneyBillWave, FaCreditCard } from 'react-icons/fa';
import axios from 'axios';
import PaymentForm from './PaymentForm';
import {
  createDonationIntent,
  confirmDonation,
  createCashDonation,
} from './donations';
import { useAuth } from '../contexts/AuthContext';

// Inizializza Stripe.js
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC, {
  locale: 'it',
});

const FBPurchaseDrawer = ({ isOpen, onClose, stockItem, amount }) => {
  const { userData } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [paymentMethod, setPaymentMethod] = useState(null); // 'electronic' o 'cash'
  const [clientSecret, setClientSecret] = useState('');
  const [localDonationId, setLocalDonationId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasConfirmed, setHasConfirmed] = useState(false);

  const drawerBg = useColorModeValue('white', 'gray.800');

  // Verifica la disponibilità del prodotto
  const checkProductAvailability = async () => {
    try {
      const response = await axios.get('/api/stocks');
      const stocks = response.data.data || [];
      const product = stocks.find((stock) => stock.Nome === stockItem);

      if (!product || !product.disponibili || product.disponibili <= 0) {
        toast({
          title: 'Prodotto Non Disponibile',
          description: `${stockItem} non è più disponibile`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        onClose();
        return false;
      }
      return true;
    } catch (err) {
      console.error('Errore verifica disponibilità:', err);
      toast({
        title: 'Errore',
        description: 'Impossibile verificare la disponibilità del prodotto',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      onClose();
      return false;
    }
  };

  // Reset quando si apre il drawer
  React.useEffect(() => {
    if (isOpen) {
      setPaymentMethod(null);
      setClientSecret('');
      setLocalDonationId('');
      setError('');
      setHasConfirmed(false);
    }
  }, [isOpen]);

  // Crea payment intent per pagamento elettronico
  const createPaymentIntent = async () => {
    setLoading(true);
    try {
      const { data } = await createDonationIntent({
        userId: userData.id,
        stripeCustomerId: userData.stripeCustomerId,
        amount: Math.round(amount * 100),
        currency: 'eur',
        donationGoalId: null,
        metadata: { donationItem: stockItem },
        type: 'one-time',
      });
      setClientSecret(data.clientSecret);
      setLocalDonationId(data.localDonationId);
      setError(null);
    } catch (err) {
      console.error('Errore creazione payment intent', err);
      setError('Impossibile iniziare il pagamento. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  // Gestisce il successo del pagamento elettronico
  const handleElectronicSuccess = async ({ paymentIntentId }) => {
    if (hasConfirmed) return;
    setHasConfirmed(true);

    try {
      // Verifica la disponibilità prima di confermare il pagamento
      const isAvailable = await checkProductAvailability();
      if (!isAvailable) {
        return;
      }

      const response = await confirmDonation({
        localDonationId,
        paymentIntentId,
      });

      if (response.data?.alreadyConfirmed) {
        toast({
          title: 'Pagamento già confermato!',
          description: 'Il tuo pagamento è stato processato automaticamente.',
          status: 'success',
          isClosable: true,
        });
      } else {
        toast({
          title: 'Pagamento completato!',
          description: `Hai acquistato ${stockItem} con successo!`,
          status: 'success',
          isClosable: true,
        });
      }

      onClose();
      setTimeout(() => {
        navigate('/account/subscriptions');
      }, 1500);
    } catch (err) {
      console.error('Errore conferma pagamento', err);
      toast({
        title: 'Errore conferma pagamento',
        status: 'error',
        isClosable: true,
      });
    }
  };

  // Gestisce la scelta del pagamento in contanti
  const handleCashPayment = async () => {
    setLoading(true);
    try {
      // Verifica la disponibilità prima di processare il pagamento
      const isAvailable = await checkProductAvailability();
      if (!isAvailable) {
        setLoading(false);
        return;
      }

      // Crea la donazione direttamente con status 'succeeded' per pagamenti in contanti
      const { data } = await createCashDonation({
        userId: userData.id,
        amount: Math.round(amount * 100),
        currency: 'eur',
        donationGoalId: null,
        metadata: {
          donationItem: stockItem,
          paymentMethod: 'cash',
        },
        type: 'one-time',
      });

      console.log('Donazione contanti creata:', data.localDonationId);

      toast({
        title: 'Acquisto Confermato!',
        description: `Hai acquistato ${stockItem} per €${amount.toFixed(2)}. Ricorda di pagare al ritiro!`,
        status: 'success',
        isClosable: true,
      });

      onClose();
      setTimeout(() => {
        navigate('/account/subscriptions');
      }, 1500);
    } catch (err) {
      console.error('Errore creazione donazione contanti', err);
      toast({
        title: 'Errore',
        description: "Errore nella conferma dell'acquisto. Riprova più tardi.",
        status: 'error',
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      size="md"
      scrollBehavior="inside"
    >
      <DrawerOverlay bg="blackAlpha.600" />
      <DrawerContent bg={drawerBg}>
        <DrawerHeader
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Heading size="md">Acquista {stockItem}</Heading>
          <IconButton
            icon={<CloseIcon />}
            aria-label="Chiudi"
            variant="ghost"
            onClick={onClose}
          />
        </DrawerHeader>
        <Divider />
        <DrawerBody p={6}>
          <VStack spacing={6} align="stretch">
            {/* Informazioni prodotto */}
            <Box
              bg="blue.50"
              p={4}
              borderRadius="md"
              border="1px solid"
              borderColor="blue.200"
            >
              <Flex align="center" mb={3}>
                <FaHeart color="#e95995" style={{ marginRight: '8px' }} />
                <Text fontSize="sm" fontWeight="semibold" color="blue.700">
                  Acquisto F&B
                </Text>
              </Flex>
              <Text fontSize="sm" color="blue.700" lineHeight="1.6">
                Stai acquistando <strong>{stockItem}</strong> per €
                {amount.toFixed(2)}. Il pagamento sostiene le attività di CIL e
                ti permette di ritirare il prodotto.
              </Text>
            </Box>

            <Box bg="gray.50" p={4} borderRadius="md">
              <Text fontSize="sm" color="gray.600">
                Prodotto:
              </Text>
              <Text fontWeight="bold" fontSize="lg">
                {stockItem}
              </Text>
              <Text fontSize="sm" color="gray.600" mt={2}>
                Prezzo:
              </Text>
              <Text fontSize="2xl" fontWeight="bold" color="red.600">
                €{amount.toFixed(2)}
              </Text>
            </Box>

            {/* Selezione metodo di pagamento */}
            {!paymentMethod && (
              <Box>
                <Heading size="sm" mb={4} color="gray.700">
                  Scegli il metodo di pagamento:
                </Heading>
                <SimpleGrid columns={1} spacing={3}>
                  <Button
                    leftIcon={<FaCreditCard />}
                    colorScheme="blue"
                    variant="outline"
                    size="lg"
                    onClick={() => {
                      setPaymentMethod('electronic');
                      createPaymentIntent();
                    }}
                    isLoading={loading}
                  >
                    Pagamento Elettronico
                  </Button>
                  <Button
                    leftIcon={<FaMoneyBillWave />}
                    colorScheme="green"
                    variant="outline"
                    size="lg"
                    onClick={() => setPaymentMethod('cash')}
                  >
                    Pagamento in Contanti
                  </Button>
                </SimpleGrid>
              </Box>
            )}

            {/* Pagamento elettronico */}
            {paymentMethod === 'electronic' && (
              <Box>
                <Flex align="center" mb={4}>
                  <FaCreditCard style={{ marginRight: '8px' }} />
                  <Text fontWeight="semibold">Pagamento Elettronico</Text>
                </Flex>

                {loading && (
                  <Flex justify="center" py={8}>
                    <Spinner size="lg" />
                  </Flex>
                )}

                {!loading && error && (
                  <Text color="red.500" textAlign="center" mb={4}>
                    {error}
                  </Text>
                )}

                {!loading && clientSecret && (
                  <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <PaymentForm onSuccess={handleElectronicSuccess} />
                  </Elements>
                )}
              </Box>
            )}

            {/* Pagamento in contanti */}
            {paymentMethod === 'cash' && (
              <Box>
                <Flex align="center" mb={4}>
                  <FaMoneyBillWave style={{ marginRight: '8px' }} />
                  <Text fontWeight="semibold">Pagamento in Contanti</Text>
                </Flex>

                <Box
                  bg="green.50"
                  p={4}
                  borderRadius="md"
                  border="1px solid"
                  borderColor="green.200"
                >
                  <Text fontSize="sm" color="green.700" mb={3}>
                    <strong>Come funziona:</strong>
                  </Text>
                  <Text fontSize="sm" color="green.700" lineHeight="1.6">
                    1. Conferma l&apos;acquisto cliccando il pulsante qui sotto
                    <br />
                    2. Ricorda di portare €{amount.toFixed(2)} quando ritiri il
                    prodotto
                    <br />
                    3. Presentati al punto di ritiro con la conferma
                    dell&apos;acquisto
                  </Text>
                </Box>

                <Button
                  colorScheme="green"
                  size="lg"
                  w="full"
                  mt={4}
                  onClick={handleCashPayment}
                  isLoading={loading}
                  loadingText="Confermando..."
                >
                  Conferma Acquisto in Contanti
                </Button>
              </Box>
            )}
          </VStack>
        </DrawerBody>
        <Divider />
        <DrawerFooter>
          <Button variant="outline" w="full" onClick={onClose}>
            Annulla
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default FBPurchaseDrawer;
